import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import VideoPlayer from "../components/VideoPlayer";
import {
  Volume2,
  VolumeX,
  Play,
  Pause,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Bot,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useUIVersion } from "../contexts/UIVersionContext";

import { Link, useNavigate } from "react-router-dom";
import "./front_landing.css"; // Import for glass card styles
import "../pages/Education/insight-center.css"; // Import for glass button styles
import { useAccountType } from "../hooks/useAccountType";
import { useAuth } from "../hooks/useAuth";
import InfoTooltip from "../components/ui/Info-tooltip";
import useCarouselImages from "../hooks/useCarouselImages";
import { db } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";

/** ============================
 *  GOOGLE SHEETS (Performance Tracker)
 *  ============================ */
const SHEET_ID = "1UA_a1Am5ElQOuuSxRmY9X6-8sJv_51z7a9EmcNdbKFs";
const API_KEY = "AIzaSyChPu7U22MBdBgMxSfcVIG6yiaHUCNZdRo";
const RANGE = "Pie Charts!A1:O200";

async function fetchSheetValues({ sheetId, apiKey, range }) {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${encodeURIComponent(
    range
  )}?key=${apiKey}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Sheets API error: ${res.statusText}`);
  const data = await res.json();
  return data.values || [];
}

/** ============================
 *  ASSET URLS (Same as original)
 *  ============================ */
const BACKGROUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/ChatGPT%20Image%20Sep%2015%2C%202025%2C%2012_54_37%20AM.png?alt=media&token=2322a57d-447c-4319-888c-8353a34fbfb9";
const TRAILER_URL =
  "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/dallas-convention-2025-recap.mp4?alt=media&token=8ba184af-14e7-4744-bfcd-a09cb145e1c6";
const REGISTER_URL = "https://wealthbuildersevents.com";

// Placeholder avatar (for leaderboard rows)
const AVATAR_URL =
  "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/rec1.png?alt=media&token=df042a0f-924f-487f-a355-85eea6cd8075";

export default function ModernFrontLanding({ collapsed = false }) {
  const { useModernUI } = useUIVersion();
  const navigate = useNavigate();
  const { isPaid } = useAccountType();
  const { user } = useAuth();
  const { contestImages: dbContestImages, recognitionImages: dbRecognitionImages, loading: loadingCarousel } = useCarouselImages();


  /* ===== Trailer ===== */
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  /* ===== User Stats (from Associate Tracker) ===== */
  const [userStats, setUserStats] = useState({
    nlr: 0,  // Net License Recruits
    tr: 0,   // Team Recruits
    pp: 0,   // Personal Points
    tp: 0,   // Team Points
    lic: 0,  // Licenses
    nl: 0,   // Net License
    tl: 0,   // Team License
    bis: 0,  // Butts in Seats
    be: 0,   // Big Event
  });
  const [loadingStats, setLoadingStats] = useState(true);

  /* ====== Leaderboard Data (from Google Sheets) ====== */
  const [loadingLB, setLoadingLB] = useState(true);
  const [errorLB, setErrorLB] = useState(null);

  const [businessPartnersSMD, setBusinessPartnersSMD] = useState([]);
  const [businessPartnersMD, setBusinessPartnersMD] = useState([]);
  const [pointsSMD, setPointsSMD] = useState([]);
  const [pointsMD, setPointsMD] = useState([]);
  const [licensesSMD, setLicensesSMD] = useState([]);
  const [licensesMD, setLicensesMD] = useState([]);
  const [conventionSMD, setConventionSMD] = useState([]);
  const [conventionMD, setConventionMD] = useState([]);

  const [activeTab, setActiveTab] = useState("bp");

  /* ===== Contest slider ===== */
  const contestImages = useMemo(() => dbContestImages, [dbContestImages]);
  const [cIndex, setCIndex] = useState(0);
  const [autoRollC, setAutoRollC] = useState(true);

  useEffect(() => {
    if (!autoRollC) return;
    const id = setInterval(
      () => setCIndex((i) => (i + 1) % contestImages.length),
      5000
    );
    return () => clearInterval(id);
  }, [autoRollC, contestImages.length]);

  /* ===== Recognition slider ===== */
  const recognitionImages = useMemo(() => dbRecognitionImages, [dbRecognitionImages]);
  const [rIndex, setRIndex] = useState(0);
  const [autoRollR, setAutoRollR] = useState(true);

  useEffect(() => {
    if (!autoRollR) return;
    const id = setInterval(
      () => setRIndex((i) => (i + 1) % recognitionImages.length),
      5000
    );
    return () => clearInterval(id);
  }, [autoRollR, recognitionImages.length]);

  /* ===== Lightbox ===== */
  const [lightbox, setLightbox] = useState({
    open: false,
    type: null,
    index: 0,
  });
  const openLightbox = (type, index = 0) =>
    setLightbox({ open: true, type, index });
  const closeLightbox = () =>
    setLightbox({ open: false, type: null, index: 0 });

  // Contest controls
  const nextContest = () => {
    setAutoRollC(false);
    setCIndex((i) => (i + 1) % contestImages.length);
  };
  const prevContest = () => {
    setAutoRollC(false);
    setCIndex((i) => (i - 1 + contestImages.length) % contestImages.length);
  };

  // Recognition controls
  const nextRec = () => {
    setAutoRollR(false);
    setRIndex((i) => (i + 1) % recognitionImages.length);
  };
  const prevRec = () => {
    setAutoRollR(false);
    setRIndex(
      (i) => (i - 1 + recognitionImages.length) % recognitionImages.length
    );
  };

  // Navigate to Onboarding Game
  const handleOnboardingClick = () => {
    navigate("/onboarding-game");
  };

  // Fetch user stats from Associate Tracker
  useEffect(() => {
    if (!user || !isPaid) {
      setLoadingStats(false);
      return;
    }

    (async () => {
      try {
        setLoadingStats(true);
        
        // Get current user's tracker data from the 'trackers' collection
        const userTrackerRef = doc(db, "trackers", user.uid);
        const userTrackerSnap = await getDoc(userTrackerRef);
        
        if (!userTrackerSnap.exists()) {
          console.log("No tracker data for user");
          setLoadingStats(false);
          return;
        }

        const userData = userTrackerSnap.data();
        const associate = userData.associate || {};
        
        // Get user's name for baseshop filtering
        const userName = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
        
        // NLR - Net License Recruits (user's own recruits count from column 9)
        const nlr = associate.recruitTtl || 0;
        
        // PP - Personal Points (from column 45k - personalPoints)
        const pp = associate.personalPoints || 0;
        
        // NL - Net License (from netLicenseAmount)
        const nl = associate.netLicenseAmount || 0;
        
        // Now get baseshop team data (all users where this user is their recruiter/leader)
        const baseshopQuery = query(
          collection(db, "trackers"),
          where("leader", "==", userName)
        );
        
        const baseshopSnap = await getDocs(baseshopQuery);
        
        let teamRecruits = 0;
        let teamPoints = 0;
        let teamLicenses = 0;
        let baseshopLicenses = 0;
        
        baseshopSnap.forEach((docSnap) => {
          const data = docSnap.data();
          const assoc = data.associate || {};
          
          // TR - Sum all team member recruits
          teamRecruits += assoc.recruitTtl || 0;
          
          // TP - Sum all team member personal points
          teamPoints += assoc.personalPoints || 0;
          
          // TL - Sum all team member net licenses
          teamLicenses += assoc.netLicenseAmount || 0;
          
          // LIC - Count baseshop members with licenses
          if (assoc.getLicense) {
            baseshopLicenses += 1;
          }
        });

        setUserStats({
          nlr,
          tr: teamRecruits,
          pp,
          tp: teamPoints,
          lic: baseshopLicenses,
          nl,
          tl: teamLicenses,
          bis: 0, // TODO: Implement Butts in Seats
          be: 0,  // TODO: Implement Big Event
        });
        
        setLoadingStats(false);
      } catch (err) {
        console.error("Error loading user stats:", err);
        setLoadingStats(false);
      }
    })();
  }, [user, isPaid]);

  // Fetch Google Sheets data for leaderboard
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchSheetValues({
          sheetId: SHEET_ID,
          apiKey: API_KEY,
          range: RANGE,
        });

        const parseNumber = (val) => {
          const n = Number(val || "0");
          return isNaN(n) ? 0 : n;
        };

        function parseTopTable(headerIdx, nameColIdx, countColIdx) {
          const list = [];
          for (let offset = 1; offset <= 10; offset++) {
            const row = rows[headerIdx + offset];
            if (!row) break;
            const name = (row[nameColIdx] || "").trim();
            if (!name) break;
            const count = parseNumber(row[countColIdx]);
            list.push({ name, count });
          }
          return list;
        }

        // SMD (rows 26–35 -> 25–34)
        setBusinessPartnersSMD(parseTopTable(25, 0, 1));
        setPointsSMD(parseTopTable(25, 3, 4));
        setLicensesSMD(parseTopTable(25, 6, 7));
        setConventionSMD(parseTopTable(25, 9, 10));

        // MD (rows 41–50 -> 40–49)
        setBusinessPartnersMD(parseTopTable(40, 0, 1));
        setPointsMD(parseTopTable(40, 3, 4));
        setLicensesMD(parseTopTable(40, 6, 7));
        setConventionMD(parseTopTable(40, 9, 10));

        setLoadingLB(false);
      } catch (err) {
        console.error("Unable to load Google Sheet:", err);
        setErrorLB(err.message || "Failed to load leaderboard");
        setLoadingLB(false);
      }
    })();
  }, []);

  const getListsForTab = (key) => {
    switch (key) {
      case "bp":
        return { smd: businessPartnersSMD, md: businessPartnersMD };
      case "pt":
        return { smd: pointsSMD, md: pointsMD };
      case "lic":
        return { smd: licensesSMD, md: licensesMD };
      case "cv":
      default:
        return { smd: conventionSMD, md: conventionMD };
    }
  };
  const { smd, md } = getListsForTab(activeTab);
  const smdTop5 = (smd || []).slice(0, 5);
  const mdTop5 = (md || []).slice(0, 5);

  return (
    <div data-modern-homepage="true" className="min-h-screen w-full relative">
      {/* Fixed Background Layer */}
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${BACKGROUND_URL})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      />

      {/* Background Blur Overlay */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-10"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.3) 100%)",
        }}
      />

      <main
        className={cn(
          "min-h-screen text-white transition-all duration-300 relative z-20 overflow-y-auto",
          // Responsive margin based on sidebar state when using modern UI
          useModernUI
            ? collapsed
              ? "ml-0 md:ml-16"
              : "ml-0 md:ml-280"
            : "ml-0",
          // Top padding for fixed header
          useModernUI ? "pt-0" : "pt-0"
        )}
      >
        {/* Hero Section */}
        <section className="relative flex flex-col items-center justify-center min-h-[50vh] px-4 pb-4 z-10">
          <div className="w-full max-w-7xl mx-auto flex flex-col items-center justify-center">
            {/* Premium Banner - Above Video */}
            <section className="hero">
              <div className="hero-inner">
                <div className="trailer-wrap">
                  <div className="trailer-frame">
                    <VideoPlayer
                      ref={videoRef}
                      className="trailer-video"
                      src={TRAILER_URL}
                      muted={muted}
                      autoPlay
                      loop
                      playsInline
                      controls
                    />
                    <button
                      className="mute-toggle"
                      onClick={() => setMuted((m) => !m)}
                    >
                      {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                  </div>

                  <div className="cta-bar">
                    <h1 className="cta-title trailer-title-single-line">
                      BREAKING BARRIERS OCT 9–11 | SALT LAKE CITY, UTAH
                    </h1>
                    <a
                      className="btn register"
                      href={REGISTER_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Register
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>

        {/* Free Users Only: Learning & Onboarding Cards */}
        {!isPaid && (
          <>
            {/* What would you like to learn more about Card */}
            <section className="px-4 pb-8">
              <div className="max-w-4xl mx-auto">
                <Card className="bg-black/20 backdrop-blur-md border-white/10">
                  <CardHeader>
                    <CardTitle className="text-yellow-400 text-2xl font-bold text-center border-b border-yellow-400/30 pb-4">
                      What would you like to learn more about?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 justify-center flex-wrap">
                      <Link
                        to="/learn/business"
                        className="ic-btn flex-1 min-w-[200px] max-w-[300px]"
                      >
                        <span className="ic-btn-label">Business Education</span>
                      </Link>
                      <Link
                        to="/learn/education"
                        className="ic-btn flex-1 min-w-[200px] max-w-[300px]"
                      >
                        <span className="ic-btn-label">Financial Education</span>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Start your onboarding process Card */}
            <section className="px-4 pb-16">
              <div className="max-w-4xl mx-auto">
                <Card className="bg-black/20 backdrop-blur-md border-white/10">
                  <CardHeader>
                    <CardTitle className="text-yellow-400 text-2xl font-bold text-center border-b border-yellow-400/30 pb-4">
                      Start your onboarding process
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex justify-center">
                    <Link
                      to="/onboarding-game"
                      className="ic-btn"
                      style={{ width: 'auto', minWidth: '200px', maxWidth: '400px' }}
                    >
                      <span className="ic-btn-label">Start Onboarding Game</span>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </section>
          </>
        )}

        <section className="px-4 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Event & Contests Card */}
              <Card className="bg-black/20 backdrop-blur-md border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between border-b border-yellow-400/30 pb-2">
                    <CardTitle className="text-yellow-400 text-xl font-bold">
                      Event & Contests
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-black/30 border-white/20 text-white hover:bg-black/50"
                        onClick={() => setAutoRollC((v) => !v)}
                      >
                        {autoRollC ? <Pause size={16} /> : <Play size={16} />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-black/30 border-white/20 text-white hover:bg-black/50"
                        onClick={() => openLightbox("contest", cIndex)}
                      >
                        <Maximize2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative aspect-[3/2] rounded-lg overflow-hidden bg-black/30">
                    <img
                      key={cIndex}
                      src={contestImages[cIndex]}
                      alt={`Contest ${cIndex + 1}`}
                      className="w-full h-full object-contain"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />

                    {/* Navigation Arrows */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white border border-white/20"
                      onClick={prevContest}
                    >
                      <ChevronLeft size={20} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white border border-white/20"
                      onClick={nextContest}
                    >
                      <ChevronRight size={20} />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recognition Card */}
              <Card className="bg-black/20 backdrop-blur-md border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between border-b border-yellow-400/30 pb-2">
                    <CardTitle className="text-yellow-400 text-xl font-bold">
                      Recognition
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-black/30 border-white/20 text-white hover:bg-black/50"
                        onClick={() => setAutoRollR((v) => !v)}
                      >
                        {autoRollR ? <Pause size={16} /> : <Play size={16} />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-black/30 border-white/20 text-white hover:bg-black/50"
                        onClick={() => openLightbox("recognition", rIndex)}
                      >
                        <Maximize2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative aspect-[3/2] rounded-lg overflow-hidden bg-black/30">
                    <img
                      key={rIndex}
                      src={recognitionImages[rIndex]}
                      alt={`Recognition ${rIndex + 1}`}
                      className="w-full h-full object-contain"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />

                    {/* Navigation Arrows */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white border border-white/20"
                      onClick={prevRec}
                    >
                      <ChevronLeft size={20} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white border border-white/20"
                      onClick={nextRec}
                    >
                      <ChevronRight size={20} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Leaderboard Section */}
        {isPaid && (
          <section className="px-4 pb-16" aria-label="Leaderboards">
            <div className="max-w-7xl mx-auto">
              <Card className="bg-black/20 backdrop-blur-md border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between border-b border-yellow-400/30 pb-2">
                    <CardTitle className="text-yellow-400 text-xl font-bold">
                      Leaderboards
                    </CardTitle>
                    <a
                      href="https://wbperformance.netlify.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400 border border-yellow-400/40 rounded-lg transition-all duration-200 text-sm font-semibold"
                    >
                      Performance Tracker →
                    </a>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Tab Buttons */}
                  <div className="flex gap-2 mb-6 flex-wrap justify-center">
                    <Button
                      variant={activeTab === "bp" ? "default" : "outline"}
                      size="sm"
                      className={
                        activeTab === "bp"
                          ? "bg-yellow-400/20 text-yellow-400 border-yellow-400/40"
                          : "bg-black/30 border-white/20 text-white hover:bg-black/50"
                      }
                      onClick={() => setActiveTab("bp")}
                    >
                      Business Partners
                    </Button>
                    <Button
                      variant={activeTab === "pt" ? "default" : "outline"}
                      size="sm"
                      className={
                        activeTab === "pt"
                          ? "bg-yellow-400/20 text-yellow-400 border-yellow-400/40"
                          : "bg-black/30 border-white/20 text-white hover:bg-black/50"
                      }
                      onClick={() => setActiveTab("pt")}
                    >
                      Points
                    </Button>
                    <Button
                      variant={activeTab === "lic" ? "default" : "outline"}
                      size="sm"
                      className={
                        activeTab === "lic"
                          ? "bg-yellow-400/20 text-yellow-400 border-yellow-400/40"
                          : "bg-black/30 border-white/20 text-white hover:bg-black/50"
                      }
                      onClick={() => setActiveTab("lic")}
                    >
                      Licenses
                    </Button>
                    <Button
                      variant={activeTab === "cv" ? "default" : "outline"}
                      size="sm"
                      className={
                        activeTab === "cv"
                          ? "bg-yellow-400/20 text-yellow-400 border-yellow-400/40"
                          : "bg-black/30 border-white/20 text-white hover:bg-black/50"
                      }
                      onClick={() => setActiveTab("cv")}
                    >
                      Convention
                    </Button>
                  </div>

                  {/* Loading/Error States */}
                  {loadingLB && (
                    <div className="text-center text-white/60 py-8">
                      Loading leaderboard data...
                    </div>
                  )}
                  {errorLB && !loadingLB && (
                    <div className="text-center text-red-400 py-8">
                      Error: {errorLB}
                    </div>
                  )}

                  {/* Leaderboard Content */}
                  {!loadingLB && !errorLB && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* SMD Column */}
                      <div>
                        <h3 className="text-yellow-400 font-semibold text-lg mb-4 text-center">
                          Top 5 SMD
                        </h3>
                        <div className="space-y-2">
                          {smdTop5.length > 0 ? (
                            smdTop5.map((row, idx) => (
                              <div
                                key={`smd-${idx}`}
                                className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/10 hover:bg-black/40 transition-all"
                              >
                                <span className="text-yellow-400 font-bold text-lg min-w-[2rem]">
                                  #{idx + 1}
                                </span>
                                <img
                                  src={AVATAR_URL}
                                  alt=""
                                  className="w-10 h-10 rounded-full border-2 border-yellow-400/30"
                                />
                                <span className="flex-1 text-white font-medium">
                                  {row.name}
                                </span>
                                <span className="text-yellow-400 font-semibold">
                                  {row.count}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-center text-white/40 py-4">
                              No data available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* MD Column */}
                      <div>
                        <h3 className="text-yellow-400 font-semibold text-lg mb-4 text-center">
                          Top 5 MD
                        </h3>
                        <div className="space-y-2">
                          {mdTop5.length > 0 ? (
                            mdTop5.map((row, idx) => (
                              <div
                                key={`md-${idx}`}
                                className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/10 hover:bg-black/40 transition-all"
                              >
                                <span className="text-yellow-400 font-bold text-lg min-w-[2rem]">
                                  #{idx + 1}
                                </span>
                                <img
                                  src={AVATAR_URL}
                                  alt=""
                                  className="w-10 h-10 rounded-full border-2 border-yellow-400/30"
                                />
                                <span className="flex-1 text-white font-medium">
                                  {row.name}
                                </span>
                                <span className="text-yellow-400 font-semibold">
                                  {row.count}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-center text-white/40 py-4">
                              No data available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* Performance Tracker - Visible for PAID users only */}
        {isPaid && (
          <section className="px-8 md:px-4 pb-16" aria-label="Quick metrics">
          <div className="max-w-7xl mx-auto">
            <div className="rounded-2xl border border-yellow-400/30 bg-black/20 backdrop-blur-xl shadow-2xl shadow-yellow-400/20 overflow-hidden transition-all duration-500 hover:shadow-yellow-400/30 hover:border-yellow-400/40">
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto text-center text-yellow-400">
                  <thead>
                    <tr className="bg-gradient-to-b from-black/70 to-black/50 backdrop-blur-md text-yellow-400 border-b border-yellow-400/40">
                      <th colSpan={4} className="px-8 md:px-4 py-4 font-bold text-xl tracking-widest relative">
                        {new Date().toLocaleString("default", { month: "long" }).toUpperCase()}
                        <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-yellow-400 to-transparent"></div>
                      </th>
                      <th colSpan={3} className="px-4 py-4 font-bold text-xl tracking-widest">
                        TOTAL
                      </th>
                    </tr>
                    <tr className="bg-gradient-to-b from-black/50 to-black/30 backdrop-blur-lg border-b border-yellow-400/30 text-sm md:text-base">
                      <td className="px-4 py-4 font-bold">
                        <div className="flex items-center justify-center gap-1">
                          PR/TR
                          <InfoTooltip content="Net License Recruit / Team Recruits" />
                        </div>
                      </td>
                      <td className="px-4 py-4 font-bold">
                        <div className="flex items-center justify-center gap-1">
                          PP/TP
                          <InfoTooltip content="Personal Points / Team Points" />
                        </div>
                      </td>
                      <td className="px-4 py-4 font-bold">
                        <div className="flex items-center justify-center gap-1">
                          LIC
                          <InfoTooltip content="License" />
                        </div>
                      </td>
                      <td className="px-4 py-4 font-bold relative">
                        <div className="flex items-center justify-center gap-1">
                          NL
                          <InfoTooltip content="Net License" />
                        </div>
                        <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-yellow-400 to-transparent"></div>
                      </td>
                      <td className="px-4 py-4 font-bold">
                        <div className="flex items-center justify-center gap-1">
                          TL
                          <InfoTooltip content="Team License" />
                        </div>
                      </td>
                      <td className="px-4 py-4 font-bold">
                        <div className="flex items-center justify-center gap-1">
                          BIS
                          <InfoTooltip content="Buts in seats" />
                        </div>
                      </td>
                      <td className="px-4 py-4 font-bold">
                        <div className="flex items-center justify-center gap-1">
                          BE
                          <InfoTooltip content="Big Event" />
                        </div>
                      </td>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      {/* NLR/TR - Two numbers separated by slash */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-r border-b border-white/5">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : `${userStats.nlr} / ${userStats.tr}`}
                        </div>
                      </td>
                      {/* PP/TP - Two numbers separated by slash */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-r border-b border-white/5">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : `${userStats.pp} / ${userStats.tp}`}
                        </div>
                      </td>
                      {/* LIC */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-r border-b border-white/5">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : userStats.lic}
                        </div>
                      </td>
                      {/* NL - With yellow divider on right */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-b border-white/5 relative">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : userStats.nl}
                        </div>
                        <div className="absolute right-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-yellow-400 to-transparent shadow-lg shadow-yellow-400/50"></div>
                      </td>
                      {/* TL */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-r border-b border-white/5">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : userStats.tl}
                        </div>
                      </td>
                      {/* BIS */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-r border-b border-white/5">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : userStats.bis}
                        </div>
                      </td>
                      {/* BE */}
                      <td className="p-5 bg-gradient-to-br from-black/40 to-black/20 hover:from-black/60 hover:to-black/30 backdrop-blur-md transition-all duration-300 border-b border-white/5">
                        <div className="text-2xl font-bold text-white drop-shadow-lg">
                          {loadingStats ? "..." : userStats.be}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Quick Metrics Pills Section */}
        {/* <section className="px-4 pb-16" aria-label="Quick metrics">
          <div className="max-w-7xl mx-auto">
            <Card className="bg-black/20 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-yellow-400 text-xl font-bold border-b border-yellow-400/30 pb-2 flex justify-between items-center">
                  <span>
                    {new Date().toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    }).toUpperCase()}{" "}
                    PERFORMANCE
                  </span>
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="overflow-x-auto rounded-xl border border-yellow-400/20 shadow-lg shadow-yellow-400/10">
                  <table className="w-full text-center text-yellow-400">
                    <thead>
                      <tr className="bg-black/40 backdrop-blur-sm text-yellow-400 border-b border-yellow-400/30">
                        <th className="px-4 py-3 font-semibold">NLR / TR</th>
                        <th className="px-4 py-3 font-semibold">PP / TP</th>
                        <th className="px-4 py-3 font-semibold">LIC</th>
                        <th className="px-4 py-3 font-semibold">NL</th>
                        <th className="px-4 py-3 font-semibold">TL</th>
                        <th className="px-4 py-3 font-semibold">BIS</th>
                        <th className="px-4 py-3 font-semibold">BE</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        {[
                          PILL_ROW2[0].count,
                          PILL_ROW1[1].count,
                          PILL_ROW1[2].count,
                          PILL_ROW2[0].count,
                          PILL_ROW2[3].count,
                          PILL_ROW2[1].count,
                          PILL_ROW2[2].count,
                        ].map((val, idx) => (
                          <td
                            key={idx}
                            className="p-4 bg-black/30 hover:bg-black/50 border border-white/5 rounded-md transition-all duration-300"
                          >
                            <div className="text-lg font-semibold text-white drop-shadow-sm">
                              {val}
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </section> */}

        {/* Lightbox Modal */}
        {lightbox.open && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={closeLightbox}
          >
            <div className="relative max-w-4xl max-h-full">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white z-10"
                onClick={closeLightbox}
              >
                <ChevronRight size={20} className="rotate-45" />
              </Button>
              <img
                src={
                  lightbox.type === "contest"
                    ? contestImages[lightbox.index]
                    : recognitionImages[lightbox.index]
                }
                alt="Expanded view"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
